import { useEffect, useMemo, useRef } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { useAuthStore } from '@/lib/stores/auth';
import { useFirebaseDoc } from '@/lib/firebase/use-firebase-doc';
import { FIREBASE_NOTIFICATIONS_COLLECTION } from '@/lib/firebase/firebase-constants';
import { env } from '@/lib/env';
import {
  getNotificationPreferences,
  getUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationsRead,
  updateNotificationPreferences,
  type ListNotificationsParams,
  type NotificationDto,
  type NotificationPreference,
  type NotificationPreferenceInput,
  type PaginatedNotifications,
} from '@/lib/notifications';

/**
 * Hierarchical query-key factory for the notifications domain.
 *
 * Keys are deliberately **workspace-agnostic** (no `workspaceId` segment): the
 * server scopes every read to the active workspace via the JWT, and the
 * workspace-switch mutation's unfiltered `qc.invalidateQueries()` (per the
 * `apps/web/CLAUDE.md` tenant-switch rule) refetches these under the new
 * workspace. Keying by workspace would leave a stale per-workspace slot live.
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown> = {}) =>
    [...notificationKeys.lists(), filters] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
  preferences: () => [...notificationKeys.all, 'preferences'] as const,
};

const FEED_PAGE_SIZE = 25;
const DROPDOWN_PAGE_SIZE = 8;

/**
 * Display/polling cap for the unread badge. Above this, the bell shows
 * `"9+"` instead of the exact count, and {@link useNotificationBumpSync}
 * stops refetching `unread-count` on every Firestore bump — once the user
 * already knows it's "a lot", each additional bump isn't worth a request.
 * Mark-read mutations and the query's own `staleTime`/mount/focus refetch are
 * untouched, so the badge still self-heals to an exact count once it drops
 * back to (or below) the cap.
 */
export const UNREAD_COUNT_DISPLAY_CAP = 9;

// ── Feed (cursor-paginated) ──────────────────────────────────────────────────

/**
 * Cursor-paginated feed. `useInfiniteQuery` drives "Load more" imperatively —
 * the cursor is a server token, not a shareable URL offset, so it lives in the
 * query cache rather than the URL. The category/readState filters (which ARE in
 * the URL) are the `filters` key segment, so switching filters is a fresh query.
 */
export function useNotificationsInfiniteQuery(
  filters: ListNotificationsParams,
  options: { enabled?: boolean; limit?: number } = {},
) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const limit = options.limit ?? FEED_PAGE_SIZE;
  return useInfiniteQuery({
    queryKey: notificationKeys.list({ ...filters, limit }),
    queryFn: ({ pageParam }) =>
      listNotifications({ ...filters, limit, cursor: pageParam ?? undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? (lastPage.cursor ?? undefined) : undefined,
    enabled: (options.enabled ?? true) && !!accessToken,
  });
}

/** Recent-notifications slice for the bell dropdown — first page only, fetched
 * lazily when the popover opens. */
export function useRecentNotificationsQuery(enabled: boolean) {
  return useNotificationsInfiniteQuery(
    {},
    { enabled, limit: DROPDOWN_PAGE_SIZE },
  );
}

// ── Unread badge count ───────────────────────────────────────────────────────

/** Server-authoritative unread count for the active workspace. Gated on a
 * token so unauthed renders (and most component tests) fire no request. */
export function useUnreadCountQuery() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => getUnreadCount(),
    enabled: !!accessToken,
    staleTime: 30_000,
  });
}

/**
 * Live badge sync. Subscribes to the per-user Firestore doc (keyed by the
 * hashed user id) in the notifications collection; the backend bumps a `bump`
 * field on every new notification. On each `bump` change we refetch the
 * server-scoped unread-count — the count itself never comes from Firestore, so
 * it stays correct across workspace switches.
 *
 * Uses `useFirebaseDoc` (not `useFirebaseData`): the per-user doc is created
 * lazily by the backend on the first notification, so the listener must attach
 * even when the doc doesn't exist yet and stay attached until it appears — the
 * opposite of the bulk-job listener, where a missing doc means "done". Gated on
 * Firebase being configured, so tests (empty env) never open a listener.
 *
 * `hasSnapshot` marks the first snapshot as the baseline (state at subscribe
 * time) so we don't refetch on mount; any *later* `bump` change is a real new
 * notification — including the very first one, when the doc goes from absent to
 * present.
 *
 * Once the last-known count is already past {@link UNREAD_COUNT_DISPLAY_CAP}
 * (the badge is showing "9+"), a further bump skips the `unread-count`
 * refetch — the exact number stops mattering once the user already sees
 * "9+", so there's no point spending a request per bump. `lists()` is still
 * invalidated so an open dropdown/feed picks up the new row.
 */
export function useNotificationBumpSync() {
  const qc = useQueryClient();
  const uid = useAuthStore((s) => s.user?.id);
  const accessToken = useAuthStore((s) => s.accessToken);

  // No hash salt needed: the doc id is the raw user id, computed identically on
  // the backend (`String(userId)`), so there's nothing to keep in sync.
  const isConfigured = Boolean(env.firebaseProjectId && env.firebaseApiKey);

  const docId = useMemo(
    () => (uid && isConfigured ? String(uid) : undefined),
    [uid, isConfigured],
  );

  const { data, hasSnapshot } = useFirebaseDoc<{ bump?: unknown }>(
    FIREBASE_NOTIFICATIONS_COLLECTION,
    docId,
    Boolean(accessToken && docId),
  );

  const bump = data?.bump;

  const baseline = useRef<unknown>(undefined);
  const baselined = useRef(false);

  // A new subscription target (e.g. a different signed-in user) starts a fresh
  // baseline; without this the prior user's `bump` would look like a change.
  useEffect(() => {
    baselined.current = false;
  }, [docId]);

  useEffect(() => {
    if (!hasSnapshot) return;
    if (!baselined.current) {
      baselined.current = true;
      baseline.current = bump;
      return;
    }
    if (baseline.current !== bump) {
      baseline.current = bump;
      const cachedCount = qc.getQueryData<{ count: number }>(
        notificationKeys.unreadCount(),
      )?.count;
      if ((cachedCount ?? 0) <= UNREAD_COUNT_DISPLAY_CAP) {
        void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
      }
      void qc.invalidateQueries({ queryKey: notificationKeys.lists() });
    }
  }, [hasSnapshot, bump, qc]);
}

// ── Mark-read mutations (optimistic) ─────────────────────────────────────────

function patchRead(
  data: InfiniteData<PaginatedNotifications> | undefined,
  matches: (n: NotificationDto) => boolean,
  readAt: string,
): InfiniteData<PaginatedNotifications> | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      data: page.data.map((n) =>
        matches(n) && n.readAt === null ? { ...n, readAt } : n,
      ),
    })),
  };
}

/**
 * Mark one notification read. Optimistic on BOTH the badge count (decrement, so
 * it drops instantly — the Firestore bump only fires on *new* notifications,
 * never on reads) and every cached feed page (set `readAt`). Reconciles on
 * settle. `silentError`: a failed mark-read is low-stakes and the optimistic
 * count is rolled back — no toast needed.
 */
export function useMarkNotificationReadMutation() {
  const qc = useQueryClient();
  return useMutation<void, Error, number, { prevCount?: { count: number } }>({
    meta: { silentError: true },
    mutationFn: (id) => markNotificationRead(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: notificationKeys.unreadCount() });
      const prevCount = qc.getQueryData<{ count: number }>(
        notificationKeys.unreadCount(),
      );
      qc.setQueryData<{ count: number }>(notificationKeys.unreadCount(), (c) =>
        c ? { count: Math.max(0, c.count - 1) } : c,
      );
      const now = new Date().toISOString();
      qc.setQueriesData<InfiniteData<PaginatedNotifications>>(
        { queryKey: notificationKeys.lists() },
        (data) => patchRead(data, (n) => n.id === id, now),
      );
      return { prevCount };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prevCount) {
        qc.setQueryData(notificationKeys.unreadCount(), ctx.prevCount);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
      void qc.invalidateQueries({ queryKey: notificationKeys.lists() });
    },
  });
}

/**
 * Mark a selected set of notifications read in one action. Optimistic, mirroring
 * `useMarkAllReadMutation`: patch the selected rows' `readAt` in every cached
 * feed page and decrement the badge by the number of selected rows that are
 * currently UNREAD (deduped by id — the feed and dropdown caches both match
 * `notificationKeys.lists()`, so counting naively would double-decrement).
 * Rolls back the count on error; reconciles both keys on settle.
 */
export function useMarkManyReadMutation() {
  const qc = useQueryClient();
  return useMutation<void, Error, number[], { prevCount?: { count: number } }>({
    meta: { successMessage: 'Notifications marked as read' },
    mutationFn: (ids) => markNotificationsRead(ids),
    onMutate: async (ids) => {
      const idSet = new Set(ids);
      await qc.cancelQueries({ queryKey: notificationKeys.unreadCount() });

      // Count distinct selected rows currently unread across all cached lists.
      const unreadSelectedIds = new Set<number>();
      for (const [, data] of qc.getQueriesData<
        InfiniteData<PaginatedNotifications>
      >({ queryKey: notificationKeys.lists() })) {
        for (const page of data?.pages ?? []) {
          for (const n of page.data) {
            if (idSet.has(n.id) && n.readAt === null) unreadSelectedIds.add(n.id);
          }
        }
      }

      const prevCount = qc.getQueryData<{ count: number }>(
        notificationKeys.unreadCount(),
      );
      qc.setQueryData<{ count: number }>(notificationKeys.unreadCount(), (c) =>
        c ? { count: Math.max(0, c.count - unreadSelectedIds.size) } : c,
      );
      const now = new Date().toISOString();
      qc.setQueriesData<InfiniteData<PaginatedNotifications>>(
        { queryKey: notificationKeys.lists() },
        (data) => patchRead(data, (n) => idSet.has(n.id), now),
      );
      return { prevCount };
    },
    onError: (_err, _ids, ctx) => {
      if (ctx?.prevCount) {
        qc.setQueryData(notificationKeys.unreadCount(), ctx.prevCount);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
      void qc.invalidateQueries({ queryKey: notificationKeys.lists() });
    },
  });
}

/** Mark every notification read. Optimistic: zero the badge + patch all cached
 * feed pages, then reconcile on settle. */
export function useMarkAllReadMutation() {
  const qc = useQueryClient();
  return useMutation<void, Error, void, { prevCount?: { count: number } }>({
    meta: { successMessage: 'All notifications marked as read' },
    mutationFn: () => markAllNotificationsRead(),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: notificationKeys.unreadCount() });
      const prevCount = qc.getQueryData<{ count: number }>(
        notificationKeys.unreadCount(),
      );
      qc.setQueryData<{ count: number }>(notificationKeys.unreadCount(), {
        count: 0,
      });
      const now = new Date().toISOString();
      qc.setQueriesData<InfiniteData<PaginatedNotifications>>(
        { queryKey: notificationKeys.lists() },
        (data) => patchRead(data, () => true, now),
      );
      return { prevCount };
    },
    onError: (_err, _v, ctx) => {
      if (ctx?.prevCount) {
        qc.setQueryData(notificationKeys.unreadCount(), ctx.prevCount);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
      void qc.invalidateQueries({ queryKey: notificationKeys.lists() });
    },
  });
}

// ── Preferences ──────────────────────────────────────────────────────────────

export function useNotificationPreferencesQuery() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery<NotificationPreference[]>({
    queryKey: notificationKeys.preferences(),
    queryFn: () => getNotificationPreferences(),
    enabled: !!accessToken,
    staleTime: 5 * 60_000,
  });
}

/**
 * Upsert a single preference key's channels (and, on DELIVERABILITY_DECLINING,
 * its threshold). Optimistic: merge the changed
 * row into the cached matrix immediately (the switch flips with no round-trip),
 * roll back on error, write the server's returned matrix on success. Same
 * cancel→snapshot→patch→rollback dance as `optimisticObjectPatch`, hand-rolled
 * here so all three notification mutations read uniformly (mark-read/mark-all
 * can't use that helper — they span two keys + infinite-list data).
 */
export function useUpdatePreferenceMutation() {
  const qc = useQueryClient();
  const key = notificationKeys.preferences();
  return useMutation<
    NotificationPreference[],
    Error,
    NotificationPreferenceInput,
    { previous?: NotificationPreference[] }
  >({
    meta: { silentError: true, successMessage: 'Notification preferences saved' },
    mutationFn: (row) => updateNotificationPreferences([row]),
    onMutate: async (row) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<NotificationPreference[]>(key);
      qc.setQueryData<NotificationPreference[]>(key, (prev) =>
        prev
          ? prev.map((p) =>
            p.key === row.key
              ? {
                ...p,
                inAppEnabled: row.inAppEnabled,
                emailEnabled: row.emailEnabled,
                ...(row.declineThresholdPct !== undefined
                  ? { declineThresholdPct: row.declineThresholdPct }
                  : {}),
              }
              : p,
          )
          : prev,
      );
      return { previous };
    },
    onError: (_err, _row, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
    onSuccess: (data) => {
      qc.setQueryData(key, data);
    },
  });
}
